import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createConversation as createConversationAPI, getConversations, deleteConversation as deleteConversationAPI, batchDeleteConversations as batchDeleteConversationsAPI } from '../api/conversation';
import { generateConversationTitle } from '../utils/conversationTitle';
import { getMessagesByConversationId } from '../api/message';
import { useAuthStore } from './authStore';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  images?: string | null;
  videos?: string | null;
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: number;
  updatedAt: number;
  isStarred: boolean;
  isTemporary?: boolean;
  serverId?: number;
  deletedAt?: number; // 软删除时间戳，有值则表示已删除
}

interface ChatState {
  // 对话列表
  conversations: Conversation[];
  // 当前对话ID
  currentConversationId: string | null;
  // 当前流式响应内容（用于打字机效果）
  streamingContent: string;
  // 是否正在流式响应
  isStreaming: boolean;
  // 主题模式：'light' | 'dark'
  theme: 'light' | 'dark';
  // AI参数设置
  aiSettings: {
    temperature: number;
    maxLength: number;
  };
  // 左侧栏折叠状态
  sidebarLeftCollapsed: boolean;
  // 右侧栏折叠状态
  sidebarRightCollapsed: boolean;

  // Actions
  createConversation: (firstMessage?: string) => Promise<string>;
  promoteTemporaryConversation: (tempId: string, firstMessage: string) => Promise<string>;
  deleteConversation: (id: string) => Promise<void>;
  batchDeleteConversations: (ids: string[]) => Promise<void>;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  setCurrentConversation: (id: string | null) => void;
  addMessage: (conversationId: string, message: Message) => void;
  updateMessage: (conversationId: string, messageId: string, content: string, images?: string | null, videos?: string | null) => void;
  deleteMessage: (conversationId: string, messageId: string) => void;
  setStreamingContent: (content: string) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  updateAISettings: (settings: Partial<ChatState['aiSettings']>) => void;
  toggleSidebarLeft: () => void;
  toggleSidebarRight: () => void;
  exportConversation: (id: string) => void;
  loadConversations: () => Promise<void>; // 从服务器加载对话列表
  loadConversationMessages: (conversationId: string) => Promise<void>; // 加载指定对话的消息并更新到 store
  clearConversations: () => void; // 清空对话列表
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      conversations: [],
      currentConversationId: null,
      streamingContent: '',
      isStreaming: false,
      theme: 'light',
      aiSettings: {
        temperature: 0.7,
        maxLength: 2000,
      },
      sidebarLeftCollapsed: false,
      sidebarRightCollapsed: false,

      createConversation: async (firstMessage?: string) => {
        // 如果没有提供第一条消息，则只在本地创建一个临时对话，不调用后端API
        if (!firstMessage) {
          const tempId = `conv_${Date.now()}`;
          const newConversation: Conversation = {
            id: tempId,
            title: '新对话',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            isStarred: false,
            isTemporary: true,
          };
          set((state) => ({
            conversations: [newConversation, ...state.conversations],
            currentConversationId: tempId,
          }));
          return tempId;
        }

        // 如果提供了第一条消息，则尝试调用后端API创建真实对话
        try {
          const response = await createConversationAPI({
            firstMessage,
          });
          
          if (response.code === 200 && response.data?.conversation) {
            const serverConversation = response.data.conversation;
            const conversationIdStr = String(serverConversation.id); // 统一转换为字符串
            const newConversation: Conversation = {
              id: conversationIdStr, // 使用字符串ID以保持一致性
              title: serverConversation.title || '新对话',
              messages: [],
              createdAt: new Date(serverConversation.create_time || Date.now()).getTime(),
              updatedAt: new Date(serverConversation.update_time || Date.now()).getTime(),
              isStarred: false,
              isTemporary: false,
              serverId: serverConversation.id, // 保存真实的数字ID
            };
            set((state) => ({
              conversations: [newConversation, ...state.conversations],
              currentConversationId: conversationIdStr,
            }));
            return conversationIdStr;
          } else {
            throw new Error(response.message || '创建对话失败');
          }
        } catch (error) {
          console.error('Failed to create conversation:', error);
          // 如果API调用失败，回退到本地创建临时对话
          const tempId = `conv_${Date.now()}`;
          const newConversation: Conversation = {
            id: tempId,
            title: '新对话',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            isStarred: false,
            isTemporary: true,
          };
          set((state) => ({
            conversations: [newConversation, ...state.conversations],
            currentConversationId: tempId,
          }));
          return tempId;
        }
      },

      promoteTemporaryConversation: async (tempId: string, firstMessage: string) => {
        try {
          const response = await createConversationAPI({
            firstMessage,
          });
          
          if (response.code === 200 && response.data?.conversation) {
            const serverConversation = response.data.conversation;
            const newId = String(serverConversation.id);
            set((state) => ({
              conversations: state.conversations.map((conv) =>
                conv.id === tempId
                  ? {
                      ...conv,
                      id: newId,
                      serverId: serverConversation.id,
                      title: serverConversation.title || generateConversationTitle(firstMessage),
                      createdAt: new Date(serverConversation.create_time || Date.now()).getTime(),
                      updatedAt: new Date(serverConversation.update_time || Date.now()).getTime(),
                      isTemporary: false,
                    }
                  : conv
              ),
              currentConversationId: newId,
            }));
            return newId;
          } else {
            throw new Error(response.message || '升级对话失败');
          }
        } catch (error) {
          console.error('Failed to promote temporary conversation:', error);
          throw error;
        }
      },

      deleteConversation: async (id) => {
        const state = get();
        const conversation = state.conversations.find((c) => String(c.id) === String(id));
        if (conversation?.serverId != null) {
          try {
            await deleteConversationAPI(conversation.serverId);
          } catch (error) {
            console.error('Failed to soft-delete conversation on server:', error);
          }
        }
        const now = Date.now();
        set((s) => {
          const updated = s.conversations.map((conv) =>
            String(conv.id) === String(id) ? { ...conv, deletedAt: now } : conv
          );
          const newCurrentId =
            String(s.currentConversationId) === String(id)
              ? updated.find((c) => !c.deletedAt)?.id ?? null
              : s.currentConversationId;
          return {
            conversations: updated,
            currentConversationId: newCurrentId ? String(newCurrentId) : null,
          };
        });
      },

      batchDeleteConversations: async (ids) => {
        const state = get();
        const serverIds = ids
          .map(id => state.conversations.find(c => String(c.id) === String(id))?.serverId)
          .filter((id): id is number => id != null);

        if (serverIds.length > 0) {
          try {
            await batchDeleteConversationsAPI(serverIds);
          } catch (error) {
            console.error('Failed to batch-delete conversations on server:', error);
          }
        }

        const now = Date.now();
        set((s) => {
          const updated = s.conversations.map((conv) =>
            ids.includes(String(conv.id)) ? { ...conv, deletedAt: now } : conv
          );
          const newCurrentId = ids.includes(String(s.currentConversationId))
            ? updated.find((c) => !c.deletedAt)?.id ?? null
            : s.currentConversationId;
          return {
            conversations: updated,
            currentConversationId: newCurrentId ? String(newCurrentId) : null,
          };
        });
      },

      updateConversation: (id, updates) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === id ? { ...conv, ...updates, updatedAt: Date.now() } : conv
          ),
        }));
      },

      setCurrentConversation: (id) => {
        set({ currentConversationId: id });
        if (!id) return;
        const state = get();
        const conversation = state.conversations.find((c) => String(c.id) === String(id));
        const needLoad =
          conversation &&
          conversation.messages.length === 0 &&
          useAuthStore.getState().isAuthenticated &&
          (conversation.serverId != null || /^\d+$/.test(String(id)));
        if (needLoad) {
          get().loadConversationMessages(id);
        }
      },

      addMessage: (conversationId, message) => {
        set((state) => ({
          conversations: state.conversations.map((conv) => {
            if (String(conv.id) !== String(conversationId)) return conv;
            const newMessages = [...conv.messages, message];
            const isFirstUserMessage = conv.messages.length === 0 && message.role === 'user';
            // 创建对话时已带 firstMessage 时后端已用 generateConversationTitle 写入标题，勿用前 30 字覆盖；与 stream 结束后的标题逻辑一致
            let title = conv.title;
            if (isFirstUserMessage && message.role === 'user') {
              if (conv.title && conv.title !== '新对话') {
                title = conv.title;
              } else {
                title = generateConversationTitle(message.content);
              }
            }
            return {
              ...conv,
              messages: newMessages,
              title,
              updatedAt: Date.now(),
              isTemporary: false,
            };
          }),
        }));
      },

      updateMessage: (conversationId, messageId, content, images, videos) => {
        set((state) => ({
          conversations: state.conversations.map((conv) => {
            if (String(conv.id) !== String(conversationId)) return conv;
            return {
              ...conv,
              messages: conv.messages.map((msg) =>
                msg.id === messageId ? { ...msg, content, images, videos } : msg
              ),
              updatedAt: Date.now(),
            };
          }),
        }));
      },

      deleteMessage: (conversationId, messageId) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  messages: conv.messages.filter((msg) => msg.id !== messageId),
                  updatedAt: Date.now(),
                }
              : conv
          ),
        }));
      },

      setStreamingContent: (content) => {
        set({ streamingContent: content });
      },

      setIsStreaming: (isStreaming) => {
        set({ isStreaming });
      },

      toggleTheme: () => {
        set((state) => ({
          theme: state.theme === 'light' ? 'dark' : 'light',
        }));
      },

      setTheme: (theme) => {
        set({ theme });
      },

      updateAISettings: (settings) => {
        set((state) => ({
          aiSettings: { ...state.aiSettings, ...settings },
        }));
      },

      toggleSidebarLeft: () => {
        set((state) => ({
          sidebarLeftCollapsed: !state.sidebarLeftCollapsed,
        }));
      },

      toggleSidebarRight: () => {
        set((state) => ({
          sidebarRightCollapsed: !state.sidebarRightCollapsed,
        }));
      },

      exportConversation: (id) => {
        const conversation = get().conversations.find((conv) => conv.id === id);
        if (!conversation) return;

        // 提取行业规范
        const extractStandards = (text: string): string[] => {
          const standards: string[] = [];
          const patterns = [
            /GB\s*\d{5}(?:-\d{4})?/gi,
            /JGJ\s*\d{3}(?:-\d{4})?/gi,
            /防水高度\s*\d+cm/gi,
            /\d+cm\s*防水/gi,
            /平整度\s*[≤≤]?\s*\d+mm/gi,
            /间距\s*\d+mm/gi,
            /垂直度\s*[≤≤]?\s*\d+mm/gi,
          ];
          
          patterns.forEach((pattern) => {
            const matches = text.match(pattern);
            if (matches) {
              standards.push(...matches);
            }
          });
          
          return Array.from(new Set(standards)); // 去重
        };

        // 生成装修问答专用格式
        const formatDecorationQA = () => {
          let content = `========================================\n`;
          content += `装修问答记录\n`;
          content += `========================================\n\n`;
          content += `对话标题：${conversation.title}\n`;
          content += `创建时间：${new Date(conversation.createdAt).toLocaleString('zh-CN')}\n`;
          content += `更新时间：${new Date(conversation.updatedAt).toLocaleString('zh-CN')}\n`;
          content += `========================================\n\n`;

          const allStandards = new Set<string>();
          
          conversation.messages.forEach((msg, index) => {
            content += `【${msg.role === 'user' ? '问题' : '回答'} ${index + 1}】\n`;
            content += `${msg.content}\n\n`;
            
            // 提取行业规范
            const standards = extractStandards(msg.content);
            standards.forEach((std) => allStandards.add(std));
            
            if (standards.length > 0) {
              content += `相关规范：${standards.join('、')}\n\n`;
            }
            content += `---\n\n`;
          });

          // 汇总所有行业规范
          if (allStandards.size > 0) {
            content += `========================================\n`;
            content += `行业规范汇总\n`;
            content += `========================================\n`;
            Array.from(allStandards).forEach((std, index) => {
              content += `${index + 1}. ${std}\n`;
            });
            content += `\n`;
          }

          content += `========================================\n`;
          content += `导出时间：${new Date().toLocaleString('zh-CN')}\n`;
          content += `========================================\n`;

          return content;
        };

        const content = formatDecorationQA();
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `装修问答_${conversation.title || '对话'}_${new Date(conversation.createdAt).toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },

      // 加载指定对话的消息列表并写入 store（点击左侧历史时用于展示右侧对话内容）
      loadConversationMessages: async (conversationId: string) => {
        try {
          const { messages: rawMessages } = await getMessagesByConversationId(conversationId);
          const messages: Message[] = (rawMessages || []).map((msg: any) => ({
            id: String(msg.id),
            role: msg.role === 'assistant' ? 'assistant' : 'user',
            content: msg.content || '',
            images: msg.images || null,
            videos: msg.videos || null,
            timestamp: new Date(msg.create_time || msg.created_at || Date.now()).getTime(),
          }));
          set((state) => ({
            conversations: state.conversations.map((conv) =>
              String(conv.id) === String(conversationId) ? { ...conv, messages } : conv
            ),
          }));
        } catch (error) {
          console.error('Failed to load conversation messages:', error);
        }
      },

      // 从服务器加载对话列表（仅登录用户）
      loadConversations: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          // 未登录用户不加载服务器对话
          return;
        }

        try {
          const response = await getConversations(50, 0);
          if (response.code === 200 && response.data?.conversations) {
            const serverConversations = response.data.conversations;
            // 转换为前端格式
            const conversations: Conversation[] = serverConversations.map((conv: any) => ({
              id: String(conv.id),
              title: conv.title || '新对话',
              messages: [], // 消息需要单独加载
              createdAt: new Date(conv.create_time || Date.now()).getTime(),
              updatedAt: new Date(conv.update_time || Date.now()).getTime(),
              isStarred: conv.is_collect === 1,
              isTemporary: false,
              serverId: conv.id,
            }));
            set({ conversations, currentConversationId: null });
          }
        } catch (error) {
          console.error('Failed to load conversations:', error);
          // 加载失败时不清空现有对话，保持用户体验
        }
      },

      // 清空对话列表
      clearConversations: () => {
        set({ conversations: [], currentConversationId: null });
      },
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        currentConversationId: state.currentConversationId,
        theme: state.theme,
        aiSettings: state.aiSettings,
        sidebarLeftCollapsed: state.sidebarLeftCollapsed,
        sidebarRightCollapsed: state.sidebarRightCollapsed,
      }),
    }
  )
);
